import { useContext, useEffect, useState } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { DocumentAccessContext, DocumentAccessGate } from '@providers/DocumentAccess';
import ProgressIndicator from '@components/ProgressIndicator';
import preloadDocument from '@helpers/preloadDocument';
import { logError } from '@providers/ErrorTracking';
import { useUser } from '@hooks';
import PDFRenderer from '@components/PDFRenderer';

type Props = {
  documentId: string;
};

const FormPDFViewer: React.FC<Props> = ({ documentId }) => {
  const [user] = useUser();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { accessToken } = useContext(DocumentAccessContext);

  const {
    data: document,
    getData: getDocumentById,
  } = useNoodleApi(tsClient.documents.getDocumentById);

  useEffect(() => {
    if (documentId) {
      getDocumentById({
        accessToken: accessToken || undefined,
        documentId,
      });
    }
  }, [documentId, accessToken, getDocumentById]);

  useEffect(() => {
    if ((accessToken || user?.id) && document?.id) {
      preloadDocument({
        accessToken,
        id: document.id,
      })
        .then(blob => {
          const typeBlob = new Blob([blob], { type: document.mimeType });
          const blobPreviewUrl = window.URL.createObjectURL(typeBlob);
          setPreviewUrl(blobPreviewUrl);
        })
        .catch(error => {
          logError(error);
        });
    }
  }, [document?.id, accessToken, user?.id, document?.mimeType]);

  if (!document || !previewUrl) {
    return <ProgressIndicator isCentered />;
  }

  return (
    <DocumentAccessGate
      documentCreatorId={document.creatorId}
      documentPersonId={document.personId}
      isCollaborator={document.isCollaborator}
      isCenteredVertically
    >
      <PDFRenderer url={previewUrl} setError={() => {}} />
    </DocumentAccessGate>
  );
};

export default FormPDFViewer;
